import { useGameState } from '../net/GameProvider.js';
import { useI18n } from '../i18n.js';
import { Icon } from './Icon.js';

interface VoiceChatBarProps {
  compact?: boolean;
}

export function VoiceChatBar({ compact = false }: VoiceChatBarProps) {
  const { state, voice } = useGameState();
  const { lang } = useI18n();
  const isMn = lang === 'mn';

  if (!state.voiceEnabled) return null;

  const isVip = state.profile?.tier === 'paid';
  const {
    voiceJoined,
    voiceMuted,
    voiceConnecting,
    isSpeaking,
    voiceError,
    audioMuted,
    toggleAudioMute,
    joinVoice,
    leaveVoice,
    toggleVoiceMute,
  } = voice;
  const participants = state.voiceParticipants;

  if (compact) {
    if (!isVip) {
      return (
        <button
          type="button"
          className="icon-button"
          onClick={toggleAudioMute}
          aria-label={audioMuted ? (isMn ? 'Дуу нээх' : 'Unmute audio') : (isMn ? 'Дуу хаах' : 'Mute audio')}
          title={audioMuted ? (isMn ? 'Дуу нээх' : 'Unmute audio') : (isMn ? 'Дуу хаах' : 'Mute audio')}
          data-tooltip={audioMuted ? (isMn ? 'Дуу нээх' : 'Unmute audio') : (isMn ? 'Дуу хаах' : 'Mute audio')}
          style={{
            position: 'relative',
            background: audioMuted ? 'rgba(248, 113, 113, 0.22)' : 'rgba(74, 222, 128, 0.16)',
            color: audioMuted ? '#fca5a5' : '#86efac',
            borderColor: audioMuted ? 'rgba(248, 113, 113, 0.5)' : 'rgba(74, 222, 128, 0.35)',
          }}
        >
          <Icon name={audioMuted ? 'volume-x' : 'volume-2'} />
          {participants.length > 0 && (
            <span
              style={{
                position: 'absolute',
                top: -4,
                right: -4,
                background: '#4ade80',
                color: '#000',
                fontSize: 10,
                fontWeight: 800,
                borderRadius: 999,
                padding: '0 4px',
                lineHeight: '14px',
              }}
            >
              {participants.length}
            </span>
          )}
        </button>
      );
    }

    if (!voiceJoined) {
      return (
        <button
          type="button"
          className="icon-button"
          onClick={() => void joinVoice()}
          disabled={voiceConnecting}
          aria-label={isMn ? 'Дуут ярианд нэгдэх' : 'Join voice chat'}
          title={isMn ? 'Дуут ярианд нэгдэх' : 'Join voice chat'}
          data-tooltip={isMn ? 'Дуут ярианд нэгдэх' : 'Join voice chat'}
          style={{
            position: 'relative',
            borderColor: participants.length > 0 ? 'rgba(74, 222, 128, 0.55)' : undefined,
          }}
        >
          <Icon name="mic" />
          {participants.length > 0 && (
            <span
              style={{
                position: 'absolute',
                top: -4,
                right: -4,
                background: '#4ade80',
                color: '#000',
                fontSize: 10,
                fontWeight: 800,
                borderRadius: 999,
                padding: '0 4px',
                lineHeight: '14px',
              }}
            >
              {participants.length}
            </span>
          )}
        </button>
      );
    }

    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <button
          type="button"
          className="icon-button"
          onClick={toggleVoiceMute}
          aria-pressed={!voiceMuted}
          aria-label={
            voiceMuted
              ? isMn
                ? 'Микрофон нээх'
                : 'Unmute microphone'
              : isMn
              ? 'Микрофон хаах'
              : 'Mute microphone'
          }
          title={
            voiceMuted
              ? isMn
                ? 'Микрофон нээх'
                : 'Unmute microphone'
              : isMn
              ? 'Микрофон хаах'
              : 'Mute microphone'
          }
          data-tooltip={
            voiceMuted
              ? isMn
                ? 'Микрофон нээх'
                : 'Unmute microphone'
              : isMn
              ? 'Микрофон хаах'
              : 'Mute microphone'
          }
          style={{
            background: voiceMuted
              ? 'rgba(248, 113, 113, 0.22)'
              : isSpeaking
              ? 'rgba(74, 222, 128, 0.42)'
              : 'rgba(74, 222, 128, 0.24)',
            color: voiceMuted ? '#fca5a5' : '#86efac',
            borderColor: voiceMuted
              ? 'rgba(248, 113, 113, 0.5)'
              : isSpeaking
              ? '#4ade80'
              : 'rgba(74, 222, 128, 0.6)',
            boxShadow: !voiceMuted && isSpeaking ? '0 0 10px rgba(74, 222, 128, 0.75)' : undefined,
            transition: 'background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
          }}
        >
          <Icon name={voiceMuted ? 'mic-off' : 'mic'} />
        </button>
        <button
          type="button"
          className="icon-button"
          onClick={leaveVoice}
          aria-label={isMn ? 'Дуут ярианаас гарах' : 'Leave voice chat'}
          title={isMn ? 'Дуут ярианаас гарах' : 'Leave voice chat'}
          data-tooltip={isMn ? 'Дуут ярианаас гарах' : 'Leave voice chat'}
          style={{
            fontSize: '0.75em',
            padding: '0 8px',
          }}
        >
          <Icon name="x" size={14} />
        </button>
      </div>
    );
  }

  if (!isVip) {
    return (
      <div
        className="voice-chat-bar"
        role="region"
        aria-label={isMn ? 'Өрөөний дуут яриа' : 'Room voice chat'}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          padding: '10px 12px',
          borderRadius: 10,
          background: 'rgba(6, 28, 20, 0.65)',
          border: '1px solid rgba(74, 222, 128, 0.28)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name={audioMuted ? 'volume-x' : 'volume-2'} size={16} />
            <span style={{ fontWeight: 600, fontSize: '0.88em', color: '#a7f3d0' }}>
              {isMn ? 'VIP Дуут яриа' : 'VIP Voice Chat'}
            </span>
            <span style={{ fontSize: '0.78em', color: '#9fb5a8' }}>
              · {participants.length} {isMn ? 'ярьж байна' : 'speaking'}
            </span>
          </div>

          <button
            type="button"
            onClick={toggleAudioMute}
            aria-pressed={!audioMuted}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              fontSize: '0.82em',
              borderRadius: 6,
              background: audioMuted ? 'rgba(248, 113, 113, 0.22)' : 'rgba(74, 222, 128, 0.22)',
              color: audioMuted ? '#fca5a5' : '#86efac',
              border: audioMuted ? '1px solid rgba(248, 113, 113, 0.5)' : '1px solid rgba(74, 222, 128, 0.55)',
              fontWeight: 600,
            }}
          >
            <Icon name={audioMuted ? 'volume-x' : 'volume-2'} size={14} />
            <span>
              {audioMuted
                ? isMn
                  ? 'Дуу нээх'
                  : 'Unmute Audio'
                : isMn
                ? 'Дуу хаах'
                : 'Mute Audio'}
            </span>
          </button>
        </div>

        {participants.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {participants.map((p) => (
              <span
                key={p.playerId}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '3px 8px',
                  borderRadius: 999,
                  fontSize: '0.78em',
                  background: p.muted ? 'rgba(255, 255, 255, 0.08)' : 'rgba(74, 222, 128, 0.16)',
                  color: p.muted ? '#94a3b8' : '#bbf7d0',
                  border: p.muted ? '1px solid rgba(255, 255, 255, 0.14)' : '1px solid rgba(74, 222, 128, 0.4)',
                }}
              >
                <Icon name={p.muted ? 'mic-off' : 'mic'} size={12} />
                <span>{p.name}</span>
              </span>
            ))}
          </div>
        ) : (
          <div
            style={{
              fontSize: '0.76em',
              color: '#9fb5a8',
              lineHeight: 1.4,
            }}
          >
            {isMn
              ? 'Одоогоор ярьж буй VIP тоглогч алга байна.'
              : 'No VIP members speaking right now.'}
          </div>
        )}

        <div
          style={{
            fontSize: '0.74em',
            color: '#6ee7b7',
            background: 'rgba(74, 222, 128, 0.06)',
            padding: '5px 8px',
            borderRadius: 6,
            border: '1px dashed rgba(74, 222, 128, 0.2)',
          }}
        >
          {isMn
            ? '🎧 Зөвхөн VIP гишүүд ярих эрхтэй. Та өрөөний яриаг сонсох эсвэл хаах боломжтой.'
            : '🎧 Only VIP members can speak. You can listen to room audio or mute at any time.'}
        </div>
      </div>
    );
  }

  return (
    <div
      className="voice-chat-bar"
      role="region"
      aria-label={isMn ? 'Өрөөний дуут яриа' : 'Room voice chat'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '10px 12px',
        borderRadius: 10,
        background: 'rgba(6, 28, 20, 0.65)',
        border: '1px solid rgba(74, 222, 128, 0.28)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name={voiceJoined && voiceMuted ? 'mic-off' : 'mic'} size={16} />
          <span style={{ fontWeight: 600, fontSize: '0.88em', color: '#a7f3d0' }}>
            {isMn ? 'Дуут яриа (VIP өрөө)' : 'Voice Chat (VIP Room)'}
          </span>
          <span style={{ fontSize: '0.78em', color: '#9fb5a8' }}>
            · {participants.length} {isMn ? 'холбогдсон' : 'connected'}
          </span>
          {voiceJoined && !voiceMuted && isSpeaking && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: '0.74em',
                color: '#4ade80',
                background: 'rgba(74, 222, 128, 0.18)',
                padding: '2px 7px',
                borderRadius: 999,
                fontWeight: 600,
                border: '1px solid rgba(74, 222, 128, 0.4)',
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: '#4ade80',
                  boxShadow: '0 0 6px #4ade80',
                }}
              />
              {isMn ? 'Ярьж байна' : 'Speaking'}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {!voiceJoined ? (
            <button
              type="button"
              onClick={() => void joinVoice()}
              disabled={voiceConnecting}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                fontSize: '0.82em',
                borderRadius: 6,
                background: 'var(--accent-color)',
                color: '#000',
                fontWeight: 700,
              }}
            >
              <Icon name="mic" size={14} />
              <span>
                {voiceConnecting
                  ? isMn
                    ? 'Холбогдож байна...'
                    : 'Connecting...'
                  : isMn
                  ? 'Ярианд орох'
                  : 'Join Voice'}
              </span>
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={toggleVoiceMute}
                aria-pressed={!voiceMuted}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 11px',
                  fontSize: '0.82em',
                  borderRadius: 6,
                  background: voiceMuted ? 'rgba(248, 113, 113, 0.22)' : 'rgba(74, 222, 128, 0.22)',
                  color: voiceMuted ? '#fca5a5' : '#86efac',
                  border: voiceMuted ? '1px solid rgba(248, 113, 113, 0.5)' : '1px solid rgba(74, 222, 128, 0.55)',
                  fontWeight: 600,
                }}
              >
                <Icon name={voiceMuted ? 'mic-off' : 'mic'} size={14} />
                <span>
                  {voiceMuted
                    ? isMn
                      ? 'Мик нээх'
                      : 'Unmute'
                    : isMn
                    ? 'Мик хаах'
                    : 'Mute'}
                </span>
              </button>
              <button
                type="button"
                onClick={leaveVoice}
                style={{
                  padding: '6px 10px',
                  fontSize: '0.8em',
                  borderRadius: 6,
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: '#e2e8f0',
                }}
              >
                {isMn ? 'Гарах' : 'Leave'}
              </button>
            </>
          )}
        </div>
      </div>

      {participants.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {participants.map((p) => (
            <span
              key={p.playerId}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '3px 8px',
                borderRadius: 999,
                fontSize: '0.78em',
                background: p.muted ? 'rgba(255, 255, 255, 0.08)' : 'rgba(74, 222, 128, 0.16)',
                color: p.muted ? '#94a3b8' : '#bbf7d0',
                border: p.muted ? '1px solid rgba(255, 255, 255, 0.14)' : '1px solid rgba(74, 222, 128, 0.4)',
              }}
            >
              <Icon name={p.muted ? 'mic-off' : 'mic'} size={12} />
              <span>{p.name}</span>
            </span>
          ))}
        </div>
      )}

      {voiceJoined && participants.length <= 1 && (
        <div
          style={{
            fontSize: '0.76em',
            color: '#a7f3d0',
            background: 'rgba(74, 222, 128, 0.07)',
            padding: '6px 10px',
            borderRadius: 6,
            border: '1px dashed rgba(74, 222, 128, 0.28)',
            lineHeight: 1.4,
          }}
        >
          {isMn
            ? '💡 Таны микрофон идэвхтэй байна. Өрөөнд өөр тоглогч дуут ярианд холбогдоход таныг сонсоно.'
            : '💡 Your microphone is active. Other players will hear you once they join voice chat.'}
        </div>
      )}

      {voiceError && (
        <div role="alert" style={{ fontSize: '0.78em', color: '#fca5a5' }}>
          {isMn
            ? 'Микрофон ашиглах зөвшөөрөл олдсонгүй. Хөтчийнхөө микрофон тохиргоог шалгана уу.'
            : 'Microphone access was denied or unavailable. Check your browser microphone permissions.'}
        </div>
      )}
    </div>
  );
}
